import { redirect } from '@tanstack/react-router';

import { HTTP_STATUS, isApiError } from '@brightlocal/api-client';

import { buildSessionExpiredLoginUrl, isSessionExpiredError } from 'shared/services';

/** Root URL segment shared by every unauthenticated auth page (login, sign-up, forgot/reset password). */
const AUTH_PATH_PREFIX = '/auth';

/**
 * Returns true when an internal path targets the auth section — exactly
 * `/auth` or `/auth` followed by a `/`, `?`, or `#` boundary. Paths that
 * merely share the prefix (`/authors`) do not match.
 *
 * @param path - Internal path (`pathname`, optionally with `?query`/`#hash`).
 */
export const isAuthPath = (path: string) => {
  if (!path.startsWith(AUTH_PATH_PREFIX)) {
    return false;
  }

  const boundary = path.charAt(AUTH_PATH_PREFIX.length);

  return boundary === '' || boundary === '/' || boundary === '?' || boundary === '#';
};

/**
 * Whether a rejection ends the session: a plain 401, or a refresh-endpoint
 * rejection tagged by `tokenRefreshService` (the endpoint uses 403 for
 * consumed/invalid refresh tokens). An untagged 403 from another endpoint
 * (e.g. an entitlement 403 from auth/me) is NOT session expiry.
 */
export const isSessionEndingError = (error: unknown) =>
  isSessionExpiredError(error) || (isApiError(error) && error.status === HTTP_STATUS.AUTHORIZATION_ERROR);

/**
 * `_authenticated` `beforeLoad` error handler: converts a session-ending
 * rejection (see {@link isSessionEndingError}) from `ensureAuthMe` into a
 * router `redirect` to the session-expired login URL, so TanStack Router
 * navigates instead of flashing the error screen while
 * `handleSessionExpired`'s hard `window.location.replace` (same URL) is still
 * in flight. Any other error (an untagged 403, network, 5xx) is rethrown
 * untouched.
 *
 * @param error - Error rejected by `ensureAuthMe`.
 * @param attemptedHref - Attempted internal href (`pathname + search + hash`), preserved for post-login return.
 */
export const redirectIfSessionExpired = (error: unknown, attemptedHref: string): never => {
  if (isSessionEndingError(error)) {
    throw redirect({ href: buildSessionExpiredLoginUrl(attemptedHref) });
  }

  throw error;
};
